import PropTypes from 'prop-types';
import React, { useState, useImperativeHandle, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import ComingSoonModal from 'components/ComingSoonModal';
import DateTimePicker from 'components/DateTimePicker';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Tooltip } from 'components/FormFields';
import TableActionButton from 'components/TableActionButton';
import useCustomerTable from './hooks/useCustomerTable';
import {
  TableCard,
  SectionHeader,
  SectionTitle,
  SectionHeading,
  ActionButtons,
  TableContainer,
  StyledTable,
  TableHeader,
  TableRow,
  TableCell,
  CustomerName,
  CustomerInfo,
  ValueText,
  FilterOverlay,
  FilterDropdown,
  FilterSection,
  FilterTitle,
  FilterRange,
  FilterInput,
  FilterOptions,
  FilterOption,
  FilterCheckbox,
  FilterActions,
  Pagination,
  PageEllipsis,
  MobileCardGrid,
  CustomerCard,
  CardHeader,
  CardHeaderContent,
  CardCustomerInfo,
  CardCustomerName,
  CardCustomerEmail,
  CardQuickInfo,
  ExpandIcon,
  CardDetails,
  CardDetailsContent,
  DetailRow,
  DetailLabel,
  DetailValue,
  CardActions,
  CardActionBtn,
  CrownIcon,
} from './StyledComponents';
import TableSkeleton from './TableSkeleton';

// Tabler crown icon component with tooltip
const SeasonPassIcon = () => {
  const theme = useTheme();

  return (
    <Tooltip text="Season Pass Holder">
      <CrownIcon>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill={theme?.colors?.primary || '#FA4616'}
          stroke="none"
        >
          <path d="M12 6l4 6l5 -4l-2 10h-14l-2 -10l5 4z" />
        </svg>
      </CrownIcon>
    </Tooltip>
  );
};

// Direction indicator component with tooltip
const DirectionIndicator = ({ direction, getTrendIndicator }) => {
  const indicator = getTrendIndicator(direction);

  return (
    <Tooltip text={indicator.label}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'help',
        }}
      >
        {indicator.icon}
      </span>
    </Tooltip>
  );
};

// Format phone number to (323) 842-1428 format
const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = ('' + phone).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[2]}) ${match[3]}-${match[4]}`;
  }
  return phone;
};

const CustomerTable = forwardRef(
  (
    { accountId, searchTerm = '', segmentFilter = '', onClearSegmentFilter },
    ref
  ) => {
    const theme = useTheme();
    const location = useLocation();
    const history = useHistory();
    const { pathname } = location;

    // Determine if we're in admin-track or admin-employee context
    const isTrackAdmin = pathname?.includes('admin-track');
    const routePrefix = isTrackAdmin ? '/admin-track' : '/admin-employee';

    const {
      customers,
      loading,
      error,
      currentPage,
      setCurrentPage,
      totalPages,
      totalCount,
      appliedFilters,
      setAppliedFilters,
    } = useCustomerTable(accountId, searchTerm, segmentFilter);

    // Expose getAppliedFilters and getTotalCount methods via ref
    useImperativeHandle(ref, () => ({
      getAppliedFilters: () => appliedFilters,
      getTotalCount: () => totalCount,
    }));

    const [filterOpen, setFilterOpen] = useState(false);
    const [expandedCards, setExpandedCards] = useState({});
    const [pendingFilters, setPendingFilters] = useState({
      minValue: '',
      maxValue: '',
      attendance: {
        high: false,
        medium: false,
        low: false,
      },
      location: {
        local: false,
        nearby: false,
        regional: false,
        distant: false,
        international: false,
      },
      dateFrom: '',
      dateTo: '',
    });

    const toggleFilterDropdown = () => {
      const newState = !filterOpen;
      setFilterOpen(newState);

      // Prevent body scroll when filter is open on mobile
      if (window.innerWidth <= 768) {
        if (newState) {
          document.body.style.overflow = 'hidden';
          document.body.style.position = 'fixed';
          document.body.style.width = '100%';
        } else {
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.width = '';
        }
      }
    };

    // Clean up body styles on unmount
    React.useEffect(() => {
      return () => {
        if (filterOpen && window.innerWidth <= 768) {
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.width = '';
        }
      };
    }, [filterOpen]);

    const toggleCard = (customerId) => {
      setExpandedCards((prev) => ({
        ...prev,
        [customerId]: !prev[customerId],
      }));
    };

    const clearFilters = () => {
      setPendingFilters({
        minValue: '',
        maxValue: '',
        attendance: {
          high: false,
          medium: false,
          low: false,
        },
        location: {
          local: false,
          nearby: false,
          regional: false,
          distant: false,
          international: false,
        },
        dateFrom: '',
        dateTo: '',
      });
      // Clear applied filters
      setAppliedFilters({
        segment: '',
        minValue: null,
        maxValue: null,
        attendanceRate: '',
        locationCategory: '',
        dateFrom: '',
        dateTo: '',
      });
      // Clear segment filter from parent component
      if (onClearSegmentFilter) {
        onClearSegmentFilter();
      }
      // Close the dropdown
      setFilterOpen(false);
    };

    const applyFilters = () => {
      // Convert UI filters to API format
      const newFilters = {
        segment: '',
        minValue: pendingFilters.minValue || null,
        maxValue: pendingFilters.maxValue || null,
        attendanceRate: '',
        locationCategory: '',
        dateFrom: pendingFilters.dateFrom,
        dateTo: pendingFilters.dateTo,
      };

      // Determine attendance rate filter
      if (
        pendingFilters.attendance.high &&
        !pendingFilters.attendance.medium &&
        !pendingFilters.attendance.low
      ) {
        newFilters.attendanceRate = 'high';
      } else if (
        !pendingFilters.attendance.high &&
        pendingFilters.attendance.medium &&
        !pendingFilters.attendance.low
      ) {
        newFilters.attendanceRate = 'medium';
      } else if (
        !pendingFilters.attendance.high &&
        !pendingFilters.attendance.medium &&
        pendingFilters.attendance.low
      ) {
        newFilters.attendanceRate = 'low';
      }

      // Determine location category filter
      if (
        pendingFilters.location.local &&
        !pendingFilters.location.nearby &&
        !pendingFilters.location.regional &&
        !pendingFilters.location.distant &&
        !pendingFilters.location.international
      ) {
        newFilters.locationCategory = 'local';
      } else if (
        !pendingFilters.location.local &&
        pendingFilters.location.nearby &&
        !pendingFilters.location.regional &&
        !pendingFilters.location.distant &&
        !pendingFilters.location.international
      ) {
        newFilters.locationCategory = 'nearby';
      } else if (
        !pendingFilters.location.local &&
        !pendingFilters.location.nearby &&
        pendingFilters.location.regional &&
        !pendingFilters.location.distant &&
        !pendingFilters.location.international
      ) {
        newFilters.locationCategory = 'regional';
      } else if (
        !pendingFilters.location.local &&
        !pendingFilters.location.nearby &&
        !pendingFilters.location.regional &&
        pendingFilters.location.distant &&
        !pendingFilters.location.international
      ) {
        newFilters.locationCategory = 'distant';
      } else if (
        !pendingFilters.location.local &&
        !pendingFilters.location.nearby &&
        !pendingFilters.location.regional &&
        !pendingFilters.location.distant &&
        pendingFilters.location.international
      ) {
        newFilters.locationCategory = 'international';
      }

      setAppliedFilters(newFilters);
      setFilterOpen(false);
    };

    const formatLocationCategory = (category) => {
      if (!category) return 'Local';
      return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
    };

    // Get trend SVG icon and color
    const getTrendIndicator = (trend) => {
      switch (trend) {
        case 'up':
          return {
            icon: (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="#10B981"
                strokeWidth="3"
                width="16"
                height="16"
              >
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            ),
            color: '#10B981', // green
            label: 'Trending up to a higher segment',
          };
        case 'down':
          return {
            icon: (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="#EF4444"
                strokeWidth="3"
                width="16"
                height="16"
              >
                <path d="M12 5v14M19 12l-7 7-7-7" />
              </svg>
            ),
            color: '#EF4444', // red
            label: 'Trending down to a lower segment',
          };
        case 'flat':
        default:
          return {
            icon: (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6B7280"
                strokeWidth="3"
                width="16"
                height="16"
              >
                <path d="M5 12h14" />
              </svg>
            ),
            color: '#6B7280', // gray
            label: 'No segment change',
          };
      }
    };

    const getBadgeVariant = (segment) => {
      switch (segment) {
        case 'Season Pass':
          return 'premium';
        case 'Super Fan':
          return 'success';
        case 'Regular Fan':
          return 'primary';
        case 'Casual Fan':
          return 'new';
        case 'Occasional':
          return 'occasional';
        case 'First Timer':
          return 'alert';
        case 'Inactive':
          return 'error';
        default:
          return 'default';
      }
    };

    // Use customers from API or show loading/error states
    const displayCustomers = customers;

    const handlePageChange = (page) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
        // Scroll to top of table
        const tableContainer = document.querySelector('.table-container');
        if (tableContainer) {
          tableContainer.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    // Generate page numbers to display
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;

      if (totalPages <= maxVisible + 2) {
        // Show all pages if total is small
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first few, ellipsis, and last page
        if (currentPage <= 3) {
          for (let i = 1; i <= maxVisible; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          pages.push(currentPage - 1);
          pages.push(currentPage);
          pages.push(currentPage + 1);
          pages.push('...');
          pages.push(totalPages);
        }
      }

      return pages;
    };

    // Mobile filter portal
    const isMobile = window.innerWidth <= 768;
    const mobileFilter = filterOpen && isMobile && (
      <>
        <FilterOverlay show={filterOpen} onClick={toggleFilterDropdown} />
        <FilterDropdown style={{ zIndex: 10001 }}>
          <div>
            <FilterSection>
              <FilterTitle>Customer Since</FilterTitle>
              <FilterRange>
                <div style={{ flex: 1 }}>
                  <DateTimePicker
                    value={pendingFilters.dateFrom || null}
                    onChange={(date) => {
                      setPendingFilters({
                        ...pendingFilters,
                        dateFrom: date || '',
                      });
                    }}
                    placeholder="Start date"
                    showTime={false}
                    cancelText="Clear"
                  />
                </div>
                <span
                  style={{
                    padding: '0 8px',
                    color: theme.name === 'dark' ? '#94a3b8' : '#64748b',
                    alignSelf: 'center',
                  }}
                >
                  to
                </span>
                <div style={{ flex: 1 }}>
                  <DateTimePicker
                    value={pendingFilters.dateTo || null}
                    onChange={(date) => {
                      setPendingFilters({
                        ...pendingFilters,
                        dateTo: date || '',
                      });
                    }}
                    placeholder="End date"
                    showTime={false}
                    cancelText="Clear"
                    minDate={pendingFilters.dateFrom || null}
                  />
                </div>
              </FilterRange>
            </FilterSection>

            <FilterSection>
              <FilterTitle>Lifetime Value Range</FilterTitle>
              <FilterRange>
                <FilterInput
                  type="number"
                  placeholder="Min $"
                  value={pendingFilters.minValue}
                  onChange={(e) =>
                    setPendingFilters({
                      ...pendingFilters,
                      minValue: e.target.value,
                    })
                  }
                />
                <span
                  style={{
                    padding: '0 8px',
                    color: theme.name === 'dark' ? '#94a3b8' : '#64748b',
                  }}
                >
                  to
                </span>
                <FilterInput
                  type="number"
                  placeholder="Max $"
                  value={pendingFilters.maxValue}
                  onChange={(e) =>
                    setPendingFilters({
                      ...pendingFilters,
                      maxValue: e.target.value,
                    })
                  }
                />
              </FilterRange>
            </FilterSection>

            <FilterSection>
              <FilterTitle>Attendance Rate</FilterTitle>
              <FilterOptions>
                <FilterOption
                  onClick={() =>
                    setPendingFilters({
                      ...pendingFilters,
                      attendance: {
                        ...pendingFilters.attendance,
                        high: !pendingFilters.attendance?.high,
                      },
                    })
                  }
                >
                  <FilterCheckbox
                    checked={pendingFilters.attendance?.high || false}
                  />
                  <span>High (70%+)</span>
                </FilterOption>
                <FilterOption
                  onClick={() =>
                    setPendingFilters({
                      ...pendingFilters,
                      attendance: {
                        ...pendingFilters.attendance,
                        medium: !pendingFilters.attendance?.medium,
                      },
                    })
                  }
                >
                  <FilterCheckbox
                    checked={pendingFilters.attendance?.medium || false}
                  />
                  <span>Medium (40-69%)</span>
                </FilterOption>
                <FilterOption
                  onClick={() =>
                    setPendingFilters({
                      ...pendingFilters,
                      attendance: {
                        ...pendingFilters.attendance,
                        low: !pendingFilters.attendance?.low,
                      },
                    })
                  }
                >
                  <FilterCheckbox
                    checked={pendingFilters.attendance?.low || false}
                  />
                  <span>Low (Below 40%)</span>
                </FilterOption>
              </FilterOptions>
            </FilterSection>

            <FilterSection>
              <FilterTitle>Location</FilterTitle>
              <FilterOptions>
                <FilterOption
                  onClick={() =>
                    setPendingFilters({
                      ...pendingFilters,
                      location: {
                        ...pendingFilters.location,
                        local: !pendingFilters.location?.local,
                      },
                    })
                  }
                >
                  <FilterCheckbox
                    checked={pendingFilters.location?.local || false}
                  />
                  <span>Local (0-10 miles)</span>
                </FilterOption>
                <FilterOption
                  onClick={() =>
                    setPendingFilters({
                      ...pendingFilters,
                      location: {
                        ...pendingFilters.location,
                        nearby: !pendingFilters.location?.nearby,
                      },
                    })
                  }
                >
                  <FilterCheckbox
                    checked={pendingFilters.location?.nearby || false}
                  />
                  <span>Nearby (11-60 miles)</span>
                </FilterOption>
                <FilterOption
                  onClick={() =>
                    setPendingFilters({
                      ...pendingFilters,
                      location: {
                        ...pendingFilters.location,
                        regional: !pendingFilters.location?.regional,
                      },
                    })
                  }
                >
                  <FilterCheckbox
                    checked={pendingFilters.location?.regional || false}
                  />
                  <span>Regional (61-100 miles)</span>
                </FilterOption>
                <FilterOption
                  onClick={() =>
                    setPendingFilters({
                      ...pendingFilters,
                      location: {
                        ...pendingFilters.location,
                        distant: !pendingFilters.location?.distant,
                      },
                    })
                  }
                >
                  <FilterCheckbox
                    checked={pendingFilters.location?.distant || false}
                  />
                  <span>Distant (101+ miles, US)</span>
                </FilterOption>
                <FilterOption
                  onClick={() =>
                    setPendingFilters({
                      ...pendingFilters,
                      location: {
                        ...pendingFilters.location,
                        international: !pendingFilters.location?.international,
                      },
                    })
                  }
                >
                  <FilterCheckbox
                    checked={pendingFilters.location?.international || false}
                  />
                  <span>International</span>
                </FilterOption>
              </FilterOptions>
            </FilterSection>

            <FilterActions>
              <button
                onClick={clearFilters}
                style={{
                  background:
                    theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : 'white',
                  color: theme.name === 'dark' ? '#94A3B8' : '#64748B',
                  border: `1px solid ${
                    theme.name === 'dark'
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(0,0,0,0.1)'
                  }`,
                  padding: '10px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background =
                    theme.name === 'dark'
                      ? 'rgba(255,255,255,0.08)'
                      : '#f3f4f6';
                  e.target.style.borderColor =
                    theme.name === 'dark'
                      ? 'rgba(255,255,255,0.2)'
                      : 'rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background =
                    theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : 'white';
                  e.target.style.borderColor =
                    theme.name === 'dark'
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(0,0,0,0.1)';
                }}
              >
                Clear
              </button>
              <button
                style={{
                  background: theme?.colors?.primary || '#FA4616',
                  color: 'white',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
                onClick={applyFilters}
              >
                Apply Filters
              </button>
            </FilterActions>
          </div>
        </FilterDropdown>
      </>
    );

    return (
      <EnhancedThemeProvider>
        {isMobile &&
          mobileFilter &&
          ReactDOM.createPortal(mobileFilter, document.body)}
        <TableCard>
          <SectionHeader>
            <SectionTitle>
              <SectionHeading>Customer Details</SectionHeading>
            </SectionTitle>
            <TableActionButton
              onFilterClick={toggleFilterDropdown}
              onClearClick={clearFilters}
            />
            {filterOpen && !isMobile && (
              <FilterDropdown>
                <div>
                  <FilterSection>
                    <FilterTitle>Lifetime Value Range</FilterTitle>
                    <FilterRange>
                      <FilterInput
                        type="number"
                        placeholder="Min $"
                        value={pendingFilters.minValue}
                        onChange={(e) =>
                          setPendingFilters({
                            ...pendingFilters,
                            minValue: e.target.value,
                          })
                        }
                      />
                      <span
                        style={{
                          color:
                            theme.name === 'dark'
                              ? theme.colors?.text?.secondaryMuted || '#94A3B8'
                              : '#64748b',
                        }}
                      >
                        to
                      </span>
                      <FilterInput
                        type="number"
                        placeholder="Max $"
                        value={pendingFilters.maxValue}
                        onChange={(e) =>
                          setPendingFilters({
                            ...pendingFilters,
                            maxValue: e.target.value,
                          })
                        }
                      />
                    </FilterRange>
                  </FilterSection>

                  <FilterSection>
                    <FilterTitle>Attendance Rate</FilterTitle>
                    <FilterOptions>
                      <FilterOption
                        onClick={() =>
                          setPendingFilters({
                            ...pendingFilters,
                            attendance: {
                              ...pendingFilters.attendance,
                              high: !pendingFilters.attendance.high,
                            },
                          })
                        }
                      >
                        <FilterCheckbox
                          checked={pendingFilters.attendance.high}
                        />
                        <span>High (70%+)</span>
                      </FilterOption>
                      <FilterOption
                        onClick={() =>
                          setPendingFilters({
                            ...pendingFilters,
                            attendance: {
                              ...pendingFilters.attendance,
                              medium: !pendingFilters.attendance.medium,
                            },
                          })
                        }
                      >
                        <FilterCheckbox
                          checked={pendingFilters.attendance.medium}
                        />
                        <span>Medium (40-69%)</span>
                      </FilterOption>
                      <FilterOption
                        onClick={() =>
                          setPendingFilters({
                            ...pendingFilters,
                            attendance: {
                              ...pendingFilters.attendance,
                              low: !pendingFilters.attendance.low,
                            },
                          })
                        }
                      >
                        <FilterCheckbox
                          checked={pendingFilters.attendance.low}
                        />
                        <span>Low (Below 40%)</span>
                      </FilterOption>
                    </FilterOptions>
                  </FilterSection>

                  <FilterSection>
                    <FilterTitle>Location</FilterTitle>
                    <FilterOptions>
                      <FilterOption
                        onClick={() =>
                          setPendingFilters({
                            ...pendingFilters,
                            location: {
                              ...pendingFilters.location,
                              local: !pendingFilters.location.local,
                            },
                          })
                        }
                      >
                        <FilterCheckbox
                          checked={pendingFilters.location.local}
                        />
                        <span>Local (0-10 miles)</span>
                      </FilterOption>
                      <FilterOption
                        onClick={() =>
                          setPendingFilters({
                            ...pendingFilters,
                            location: {
                              ...pendingFilters.location,
                              nearby: !pendingFilters.location.nearby,
                            },
                          })
                        }
                      >
                        <FilterCheckbox
                          checked={pendingFilters.location.nearby}
                        />
                        <span>Nearby (11-60 miles)</span>
                      </FilterOption>
                      <FilterOption
                        onClick={() =>
                          setPendingFilters({
                            ...pendingFilters,
                            location: {
                              ...pendingFilters.location,
                              regional: !pendingFilters.location.regional,
                            },
                          })
                        }
                      >
                        <FilterCheckbox
                          checked={pendingFilters.location.regional}
                        />
                        <span>Regional (61-100 miles)</span>
                      </FilterOption>
                      <FilterOption
                        onClick={() =>
                          setPendingFilters({
                            ...pendingFilters,
                            location: {
                              ...pendingFilters.location,
                              distant: !pendingFilters.location.distant,
                            },
                          })
                        }
                      >
                        <FilterCheckbox
                          checked={pendingFilters.location.distant}
                        />
                        <span>Distant (101+ miles, US)</span>
                      </FilterOption>
                      <FilterOption
                        onClick={() =>
                          setPendingFilters({
                            ...pendingFilters,
                            location: {
                              ...pendingFilters.location,
                              international:
                                !pendingFilters.location.international,
                            },
                          })
                        }
                      >
                        <FilterCheckbox
                          checked={pendingFilters.location.international}
                        />
                        <span>International</span>
                      </FilterOption>
                    </FilterOptions>
                  </FilterSection>

                  <FilterSection>
                    <FilterTitle>Customer Since</FilterTitle>
                    <FilterRange>
                      <div style={{ flex: 1 }}>
                        <DateTimePicker
                          value={pendingFilters.dateFrom || null}
                          onChange={(date) => {
                            setPendingFilters({
                              ...pendingFilters,
                              dateFrom: date || '',
                            });
                          }}
                          placeholder="Start date"
                          showTime={false}
                          cancelText="Clear"
                        />
                      </div>
                      <span
                        style={{
                          color:
                            theme.name === 'dark'
                              ? theme.colors?.text?.secondaryMuted || '#94A3B8'
                              : '#64748b',
                          alignSelf: 'center',
                        }}
                      >
                        to
                      </span>
                      <div style={{ flex: 1 }}>
                        <DateTimePicker
                          value={pendingFilters.dateTo || null}
                          onChange={(date) => {
                            setPendingFilters({
                              ...pendingFilters,
                              dateTo: date || '',
                            });
                          }}
                          placeholder="End date"
                          showTime={false}
                          cancelText="Clear"
                          minDate={pendingFilters.dateFrom || null}
                        />
                      </div>
                    </FilterRange>
                  </FilterSection>

                  <FilterActions>
                    <button
                      className="btn btn-secondary"
                      style={{ flex: 1 }}
                      onClick={clearFilters}
                    >
                      Clear
                    </button>
                    <button
                      className="btn btn-primary"
                      style={{ flex: 1 }}
                      onClick={applyFilters}
                    >
                      Apply Filters
                    </button>
                  </FilterActions>
                </div>
              </FilterDropdown>
            )}
          </SectionHeader>

          {loading && <TableSkeleton rows={8} />}

          {error && (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#EF4444',
              }}
            >
              Error loading customers: {error.message}
            </div>
          )}

          {!loading && !error && displayCustomers.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '80px 20px',
                color:
                  theme.name === 'dark'
                    ? theme.colors?.text?.secondaryMuted || '#94A3B8'
                    : '#64748b',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="48"
                height="48"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{
                  marginBottom: '16px',
                  opacity: 0.5,
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  marginBottom: '8px',
                }}
              >
                No customers found
              </div>
              <div style={{ fontSize: '14px', opacity: 0.8 }}>
                Try adjusting your filters or search terms
              </div>
            </div>
          )}

          {!loading && !error && displayCustomers.length > 0 && (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <TableHeader>Customer</TableHeader>
                    <TableHeader>Segment</TableHeader>
                    <TableHeader>LTV</TableHeader>
                    <TableHeader>Events YTD</TableHeader>
                    <TableHeader>Location</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {displayCustomers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>
                        <CustomerName>
                          {customer.name}
                          {customer.has_season_pass && <SeasonPassIcon />}
                        </CustomerName>
                        <CustomerInfo>
                          {customer.phone ? (
                            <a
                              href={`tel:${customer.phone}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              {formatPhone(customer.phone)}
                            </a>
                          ) : (
                            'No phone'
                          )}
                        </CustomerInfo>
                        <CustomerInfo style={{ marginTop: '4px' }}>
                          Since {customer.since}
                        </CustomerInfo>
                      </TableCell>
                      <TableCell>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <Badge variant={getBadgeVariant(customer.segment)}>
                            {customer.segment}
                          </Badge>
                          {customer.segment_direction && (
                            <DirectionIndicator
                              direction={customer.segment_direction}
                              getTrendIndicator={getTrendIndicator}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <ValueText>{customer.lifetimeValue}</ValueText>
                      </TableCell>
                      <TableCell>
                        <ValueText>
                          {customer.eventsAttended} of {customer.totalEvents}
                        </ValueText>
                      </TableCell>
                      <TableCell>
                        <Badge variant="location">
                          {formatLocationCategory(customer.locationCategory)}
                        </Badge>
                        <CustomerInfo style={{ marginTop: '4px' }}>
                          {customer.distance} miles
                        </CustomerInfo>
                      </TableCell>
                      <TableCell>
                        <ActionButtons>
                          <ComingSoonModal
                            title="Customer Messaging"
                            description="We're building a powerful messaging system to help you connect with your customers."
                            features={[
                              'Message an individual customer directly',
                              'View history of all past conversations',
                              'Get notifications and inbox for customer messages',
                            ]}
                          >
                            <ButtonV2
                              variant="icon"
                              title="Message"
                              icon={
                                <svg
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
                                </svg>
                              }
                            />
                          </ComingSoonModal>
                          <ButtonV2
                            variant="icon"
                            as={Link}
                            to={`${routePrefix}/users/transactions/${customer.user_id}`}
                            title="View Details"
                            icon={
                              <svg
                                width="16"
                                height="16"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                                <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                              </svg>
                            }
                          />
                          <ButtonV2
                            variant="icon"
                            title="Add Credit"
                            onClick={() =>
                              history.push(
                                `${routePrefix}/customers/issue-credit/${customer.user_id}`
                              )
                            }
                            icon={
                              <svg
                                width="16"
                                height="16"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                              </svg>
                            }
                          />
                        </ActionButtons>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}

          {/* Mobile Card View - Collapsible */}
          {!loading && !error && displayCustomers.length > 0 && (
            <MobileCardGrid>
              {displayCustomers.map((customer) => {
                const isExpanded = expandedCards[customer.id];
                return (
                  <CustomerCard key={customer.id} expanded={isExpanded}>
                    <CardHeader onClick={() => toggleCard(customer.id)}>
                      <CardHeaderContent>
                        <CardCustomerInfo>
                          <CardCustomerName>
                            {customer.name}
                            {customer.has_season_pass && <SeasonPassIcon />}
                          </CardCustomerName>
                          <CardCustomerEmail>
                            {formatPhone(customer.phone) || 'No phone'}
                          </CardCustomerEmail>
                        </CardCustomerInfo>
                        <CardQuickInfo>
                          <ExpandIcon
                            expanded={isExpanded}
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                          </ExpandIcon>
                        </CardQuickInfo>
                      </CardHeaderContent>
                    </CardHeader>
                    <CardDetails expanded={isExpanded}>
                      <CardDetailsContent>
                        <DetailRow>
                          <DetailLabel>Segment</DetailLabel>
                          <DetailValue>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <Badge
                                variant={getBadgeVariant(customer.segment)}
                              >
                                {customer.segment}
                              </Badge>
                              {customer.segment_direction && (
                                <DirectionIndicator
                                  direction={customer.segment_direction}
                                  getTrendIndicator={getTrendIndicator}
                                />
                              )}
                            </div>
                          </DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Lifetime Value</DetailLabel>
                          <DetailValue>
                            ${customer.lifetimeValue.toLocaleString()} ($
                            {customer.avgPerEvent}/event avg)
                          </DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Events YTD</DetailLabel>
                          <DetailValue>
                            {customer.eventsAttended} of {customer.totalEvents}{' '}
                            ({customer.attendance}% attendance)
                          </DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Location</DetailLabel>
                          <DetailValue>
                            {formatLocationCategory(customer.locationCategory)}{' '}
                            ({customer.distance} miles)
                          </DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Customer Since</DetailLabel>
                          <DetailValue>{customer.since}</DetailValue>
                        </DetailRow>
                      </CardDetailsContent>
                      <CardActions>
                        <CardActionBtn>
                          <svg
                            width="14"
                            height="14"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
                          </svg>
                          Message
                        </CardActionBtn>
                        <CardActionBtn
                          as={Link}
                          to={`${routePrefix}/users/transactions/${customer.user_id}`}
                        >
                          <svg
                            width="14"
                            height="14"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                            <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                          </svg>
                          View
                        </CardActionBtn>
                        <CardActionBtn
                          onClick={() =>
                            history.push(
                              `${routePrefix}/customers/issue-credit/${customer.user_id}`
                            )
                          }
                        >
                          <svg
                            width="14"
                            height="14"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                          </svg>
                          Credit
                        </CardActionBtn>
                      </CardActions>
                    </CardDetails>
                  </CustomerCard>
                );
              })}
            </MobileCardGrid>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination>
              <ButtonV2
                variant="icon"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                icon={
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
                    />
                  </svg>
                }
              />

              {getPageNumbers().map((page, index) =>
                page === '...' ? (
                  <PageEllipsis key={`ellipsis-${index}`}>...</PageEllipsis>
                ) : (
                  <ButtonV2
                    variant="icon"
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </ButtonV2>
                )
              )}

              <ButtonV2
                variant="icon"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                icon={
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
                    />
                  </svg>
                }
              />
            </Pagination>
          )}
        </TableCard>
      </EnhancedThemeProvider>
    );
  }
);

CustomerTable.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  searchTerm: PropTypes.string,
  segmentFilter: PropTypes.string,
  onClearSegmentFilter: PropTypes.func,
};

export default CustomerTable;
